import { useContext, useMemo } from "react";
import { CommissionStatus, PayoutStatus } from "types/affiliation.types";
import { affiliationService } from "app/services/affiliation.service";
import { AffiliationContext } from "features/affiliation/Affiliation.context";
import { useEarnings } from "features/affiliation/common/hooks";
import { sumEarnings } from "features/affiliation/NewLook/useEarningsHistory";

/**
 * The money figures of the affiliation pages, in one place so they always
 * reconcile: total earnings = confirmed + awaiting confirmation, and the
 * balance is what is confirmed and not yet paid out or requested.
 */
const useAffiliationTotals = () => {
  const { balance } = useContext(AffiliationContext)!;
  const { commissions, commissionHistoryQuery } = useEarnings();
  const payoutsHistoryQuery = affiliationService.useGetPayoutsHistoryQuery();
  const payouts = payoutsHistoryQuery.data;

  return useMemo(() => {
    const sumCommissions = (status: CommissionStatus) =>
      (commissions || [])
        .filter((commission) => commission.status === status)
        .reduce((sum, commission) => sum + commission.commission, 0);
    const payoutsWith = (status: PayoutStatus) =>
      (payouts || []).filter((payout) => payout.status === status);
    const sumPayouts = (status: PayoutStatus) =>
      payoutsWith(status).reduce((sum, payout) => sum + payout.amount, 0);

    return {
      balance: balance ?? 0,
      totalEarnings: sumEarnings(commissions),
      confirmed: sumCommissions(CommissionStatus.APPROVED),
      awaitingConfirmation: sumCommissions(CommissionStatus.PENDING),
      commissionsCount: commissions?.length ?? 0,
      paidOut: sumPayouts(PayoutStatus.APPROVED),
      processedPayouts: payoutsWith(PayoutStatus.APPROVED).length,
      awaitingApproval: sumPayouts(PayoutStatus.PENDING),
      pendingPayouts: payoutsWith(PayoutStatus.PENDING).length,
      loading:
        commissionHistoryQuery.isLoading || payoutsHistoryQuery.isLoading,
    };
  }, [
    balance,
    commissions,
    payouts,
    commissionHistoryQuery.isLoading,
    payoutsHistoryQuery.isLoading,
  ]);
};

export default useAffiliationTotals;
